import { createPortal } from 'react-dom';
import { X, FileText, Calendar, DollarSign, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import { getPaystubsForResource, Paystub } from '../lib/payrollRuns';
import PaystubView from './payroll/PaystubView';

interface ResourcePaystubsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceId: string;
  businessId: string;
  resourceInfo: {
    name: string;
    email: string;
    avatarUrl?: string;
  };
}

type PaystubWithRunInfo = Paystub & {
  run_number: number;
  period_start_date: string;
  period_end_date: string;
  run_date: string;
};

export default function ResourcePaystubsModal({
  isOpen,
  onClose,
  resourceId,
  businessId,
  resourceInfo
}: ResourcePaystubsModalProps) {
  const [paystubs, setPaystubs] = useState<PaystubWithRunInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPaystubs, setExpandedPaystubs] = useState<Set<string>>(new Set());
  const [viewingPaystub, setViewingPaystub] = useState<PaystubWithRunInfo | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // Open animation
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isVisible && !isOpen) {
      setIsAnimating(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 200);
  };

  useEffect(() => {
    if (isOpen && resourceId && businessId) {
      loadPaystubs();
    }
  }, [isOpen, resourceId, businessId]);

  const loadPaystubs = async () => {
    try {
      setLoading(true);
      const data = await getPaystubsForResource(resourceId, businessId);
      setPaystubs(data);
    } catch (error) {
      console.error('Error loading paystubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePaystub = (paystubId: string) => {
    setExpandedPaystubs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(paystubId)) {
        newSet.delete(paystubId);
      } else {
        newSet.add(paystubId);
      }
      return newSet;
    });
  };

  const handleViewPaystub = (paystub: PaystubWithRunInfo) => {
    setViewingPaystub(paystub);
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {createPortal(
        <div 
          className={`fixed inset-0 transition-opacity duration-200 ${
            isVisible ? 'bg-black/50' : 'bg-black/0'
          }`}
          style={{ 
            zIndex: 100200,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
          onClick={handleClose}
          onTransitionEnd={handleTransitionEnd}
        >
          <div 
            className={`bg-white transform transition-all duration-200 ${
              isMobile 
                ? `fixed left-0 right-0 w-full ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
                : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                    isVisible ? 'translate-x-0' : 'translate-x-full'
                  }`
            }`}
            style={{ 
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100210,
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              ...(isMobile && { left: 0, right: 0 }),
              ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="h-full flex flex-col overflow-hidden">
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>

              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative flex-shrink-0">
                <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                  My Paystubs
                </h2>
                <p className="text-sm text-gray-500 mt-1">{resourceInfo.name}</p>
              </div>

              {/* Content */}
              <div 
                className="flex-1 overflow-y-auto bg-white"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
                  {loading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                      <span className="ml-3 text-gray-600">Loading paystubs...</span>
                    </div>
                  ) : paystubs.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                        <FileText className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Paystubs Available</h3>
                      <p className="text-sm text-gray-500 max-w-xs mx-auto">
                        You don't have any paystubs yet. Paystubs will appear here after payroll is processed.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paystubs.map((paystub) => (
                        <div 
                          key={paystub.id} 
                          className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden"
                        >
                          {/* Paystub Header */}
                          <button
                            onClick={() => togglePaystub(paystub.id)}
                            className="w-full p-4 hover:bg-gray-50 active:bg-gray-100 active:scale-[0.99] transition-all duration-150 flex items-center justify-between touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-green-100 rounded-[10px]">
                                <FileText className="h-5 w-5 text-green-600" />
                              </div>
                              <div className="text-left">
                                <p className="font-semibold text-gray-900 text-[14px]">
                                  Paystub #{paystub.run_number.toString().padStart(4, '0')}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                  <Calendar className="h-3 w-3" />
                                  <span>
                                    {format(new Date(paystub.period_start_date), 'MMM d')} -{' '}
                                    {format(new Date(paystub.period_end_date), 'MMM d, yyyy')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Net Pay</p>
                                <p className="font-bold text-green-600 text-lg">
                                  ${paystub.net_pay.toFixed(2)}
                                </p>
                              </div>
                              <ChevronDown
                                className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                                  expandedPaystubs.has(paystub.id) ? 'rotate-180' : ''
                                }`}
                              />
                            </div>
                          </button>

                          {/* Paystub Details */}
                          {expandedPaystubs.has(paystub.id) && (
                            <div className="px-4 pb-4 pt-0 space-y-3 border-t border-gray-100">
                              <div className="pt-3" />
                              {/* Earnings Summary */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 p-3 rounded-xl border border-gray-200/60">
                                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                                    <DollarSign className="h-3.5 w-3.5" />
                                    <p className="text-xs font-medium">Gross Earnings</p>
                                  </div>
                                  <p className="text-lg font-bold text-gray-900">
                                    ${paystub.gross_earnings.toFixed(2)}
                                  </p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-xl border border-gray-200/60">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Total Deductions</p>
                                  <p className="text-lg font-bold text-red-600">
                                    ${paystub.total_deductions.toFixed(2)}
                                  </p>
                                </div>
                              </div>

                              {/* Deductions Breakdown */}
                              {paystub.total_deductions > 0 && (
                                <div className="bg-gray-50 p-3 rounded-xl border border-gray-200/60">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Deductions</p>
                                  <div className="space-y-1.5 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Tax</span>
                                      <span className="font-medium text-gray-900">${paystub.tax_deductions.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">EI</span>
                                      <span className="font-medium text-gray-900">${paystub.ei_deductions.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">CPP</span>
                                      <span className="font-medium text-gray-900">${paystub.cpp_deductions.toFixed(2)}</span>
                                    </div>
                                    {paystub.other_deductions > 0 && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Other</span>
                                        <span className="font-medium text-gray-900">${paystub.other_deductions.toFixed(2)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Net Pay Highlight */}
                              <div className="bg-green-50 p-3 rounded-xl border border-green-200/60">
                                <div className="flex justify-between items-center">
                                  <p className="text-sm font-semibold text-green-900">Net Pay</p>
                                  <p className="text-xl font-bold text-green-600">
                                    ${paystub.net_pay.toFixed(2)}
                                  </p>
                                </div>
                              </div>

                              {/* View Full Paystub Button */}
                              <button
                                onClick={() => handleViewPaystub(paystub)}
                                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] text-white rounded-xl font-semibold transition-all duration-150 flex items-center justify-center gap-2 touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <FileText className="h-4 w-4" />
                                View Full Paystub
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {viewingPaystub && (
        <PaystubView
          paystub={viewingPaystub}
          resourceInfo={{
            name: resourceInfo.name,
            email: resourceInfo.email
          }}
          runNumber={viewingPaystub.run_number}
          onClose={() => setViewingPaystub(null)}
        />
      )}
    </>
  );
}
